import { Component, ReactNode } from "react";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faHome } from "@fortawesome/pro-solid-svg-icons";

import { EmptyState } from "src/design-system/EmptyState";

type FallbackRender = (props: { resetErrorBoundary: () => void }) => ReactNode;

type Props = {
  children: ReactNode;
  fallbackErrorContent?: ReactNode | FallbackRender;
};

// Reporting for boundary-caught errors lives in the root onCaughtError handler
// (src/instrumentation/clientErrorReporting.ts), not here — a componentDidCatch
// call would double-report every caught error.
export class ErrorBoundary extends Component<Props, { hasError: boolean }> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      const { fallbackErrorContent } = this.props;

      if (typeof fallbackErrorContent === "function") {
        return fallbackErrorContent({
          resetErrorBoundary: this.resetErrorBoundary,
        });
      }

      return (
        fallbackErrorContent || (
          <div className="flex h-full items-center justify-center">
            <EmptyState
              actionButton={{
                label: "Go to home page",
                icon: faHome,
                onClick: () => window.location.assign("/"),
              }}
              dataLoc="error-boundary"
              description="Oops, something went wrong."
              headline="Error"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        )
      );
    }

    return this.props.children;
  }
}
